import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  isCreditWalletCashWallet,
  isLegacyRefundPromoWallet,
} from "./constants";
import type { ResultType } from "./types";
import type { WizardBetTargetedPreset } from "./Campaigns/CreateCampaignWizard/betTargetedPresets";

/**
 * FE-only product → backend map for the Dynamic Bonus Builder (BONENG-788).
 * Operators see natural-language labels; PAM still submits `type_key` +
 * `result_type` (and optional `bet_targeted_preset`) unchanged.
 */

export type BonusProductCategory = "sportsbook" | "casino" | "general";

export type BonusTypeDefinition = {
  id: string;
  category: BonusProductCategory;
  label: string;
  type_key: string;
  result_type: ResultType;
  bet_targeted_preset?: WizardBetTargetedPreset;
};

export const BONUS_PRODUCT_CATEGORY_LABELS: Record<BonusProductCategory, string> =
  {
    sportsbook: AppStrings.PAM_DYNAMIC_BONUS_TYPE_CATEGORY_SPORTSBOOK,
    casino: AppStrings.PAM_DYNAMIC_BONUS_TYPE_CATEGORY_CASINO,
    general: AppStrings.PAM_DYNAMIC_BONUS_TYPE_CATEGORY_GENERAL,
  };

/**
 * Product taxonomy for Sportsbook / Casino / General bonus types. Preset
 * entries share a step-2 `type_key` with simpler awards (e.g. Bet & Get →
 * `award_free_picks`); `bet_targeted_preset` distinguishes the blueprint.
 */
export const BONUS_TYPE_CATALOG: readonly BonusTypeDefinition[] = [
  {
    id: "free_pick",
    category: "sportsbook",
    label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FREE_PICK,
    type_key: "award_free_picks",
    result_type: "award",
  },
  {
    id: "profit_boost",
    category: "sportsbook",
    label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_PROFIT_BOOST,
    type_key: "create_profit_boost_promotion",
    result_type: "award",
  },
  {
    id: "bet_and_get",
    category: "sportsbook",
    label: AppStrings.PAM_DYNAMIC_BONUS_TYPE_BET_AND_GET,
    type_key: "award_free_picks",
    result_type: "award",
    bet_targeted_preset: "bet_and_get",
  },
  {
    id: "no_sweat_bet",
    category: "sportsbook",
    label: AppStrings.DYNAMIC_BONUS_PRESET_NO_SWEAT_BET,
    type_key: "refund_on_loss",
    result_type: "award",
    bet_targeted_preset: "no_sweat_bet",
  },
  {
    id: "parlay_insurance",
    category: "sportsbook",
    label: AppStrings.DYNAMIC_BONUS_PRESET_PARLAY_INSURANCE,
    type_key: "refund_on_loss",
    result_type: "award",
    bet_targeted_preset: "parlay_insurance",
  },
  {
    id: "free_spin",
    category: "casino",
    label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FREE_SPIN,
    type_key: "award_free_spins",
    result_type: "award",
  },
  {
    id: "casino_cash",
    category: "casino",
    label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASINO_CASH,
    type_key: "credit_wallet",
    result_type: "award",
  },
  {
    id: "cashback",
    category: "general",
    label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASHBACK,
    type_key: "award_cashback",
    result_type: "award",
  },
];

/**
 * Built per call so TCM strings that arrive after boot (and language switches)
 * are picked up. Module-scope `AppStrings.X` snapshots freeze English defaults
 * before `loadAppStringsForLanguage` / `setAppString` rebind AppStrings.
 * `credit_wallet` stays authoring-hidden via `isAuthorable()` until Casino Cash
 * gets a RESULT_PARAM_SCHEMA; it still needs a display name for existing steps.
 */
const resultTypeKeyLabels = (): Readonly<Record<string, string>> => ({
  show_bonus: AppStrings.PAM_DYNAMIC_BONUS_RESULT_BONUS_OFFER,
  send_notification: AppStrings.PAM_DYNAMIC_BONUS_RESULT_SEND_NOTIFICATION,
  award_free_picks: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FREE_PICK,
  create_profit_boost_promotion: AppStrings.PAM_DYNAMIC_BONUS_RESULT_PROFIT_BOOST,
  award_free_spins: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FREE_SPIN,
  award_free_cash: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASH,
  refund_on_loss: AppStrings.PAM_DYNAMIC_BONUS_RESULT_REFUND_ON_LOSS,
  credit_wallet: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASINO_CASH,
  create_deposit_match_promotion: AppStrings.PAM_DYNAMIC_BONUS_RESULT_DEPOSIT_MATCH,
  award_bonus_bets: AppStrings.PAM_DYNAMIC_BONUS_RESULT_BONUS_BET,
  award_future_bonus: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FUTURE_BONUS,
  award_cashback: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASHBACK,
  display_banner: AppStrings.PAM_DYNAMIC_BONUS_RESULT_DISPLAY_BANNER,
});

export type ResultLabelOptions = {
  creditWalletCashResult?: boolean;
  primaryCurrencyName?: string;
  /** Stored `wallet` on this step — used so legacy Free Pick campaigns keep
   * the Refund on Loss name while new Credit Wallet steps show the rename. */
  storedWallet?: unknown;
  /** True when this step is already authored as `refund_on_loss`. An omitted
   * wallet on that step is the historical Free Pick default, not a new pick. */
  refundAlreadyAuthored?: boolean;
};

/** BONENG-1236: "Credit Wallet {primary currency name}". */
export const creditWalletResultLabel = (currencyName?: string): string =>
  AppStrings.PAM_DYNAMIC_BONUS_RESULT_CREDIT_WALLET.replace(
    "{currency}",
    (currencyName ?? "").trim() || AppStrings.PRIMARY_CURRENCY,
  );

const creditWalletOverride = (
  typeKey: string,
  options?: ResultLabelOptions,
  mode: "picker" | "display" = "display",
): string | undefined => {
  if (typeKey !== "refund_on_loss" || !options?.creditWalletCashResult) {
    return undefined;
  }
  // Review / StepCard: only rename when the engine will actually cash-credit.
  if (mode === "display" && !isCreditWalletCashWallet(options.storedWallet)) {
    return undefined;
  }
  // Picker: Credit Wallet for a new pick. Keep Refund on Loss when this step
  // is already refund_on_loss with a promo wallet or an omitted wallet
  // (BE defaults omitted to Free Pick).
  if (
    mode === "picker" &&
    (isLegacyRefundPromoWallet(options.storedWallet) ||
      (options.refundAlreadyAuthored &&
        !isCreditWalletCashWallet(options.storedWallet)))
  ) {
    return undefined;
  }
  return creditWalletResultLabel(options.primaryCurrencyName);
};

/** Always-on NL label for review / detail surfaces (BONENG-881). */
export const resultDisplayLabel = (
  typeKey: string,
  options?: ResultLabelOptions,
): string =>
  creditWalletOverride(typeKey, options, "display") ??
  resultTypeKeyLabels()[typeKey] ??
  typeKey;

/** Legacy Result option label: `type_key` or `type_key (result_type)`. */
export const legacyResultOptionLabel = (
  typeKey: string,
  resultType: string,
): string =>
  typeKey === "show_bonus" ? typeKey : `${typeKey} (${resultType})`;

/**
 * Label for a catalog Result option. When `useNaturalLanguage` is true,
 * operators never see the `(offer)` / `(award)` suffix — mapped keys get a
 * product NL name; unmapped keys fall back to bare `type_key`.
 */
export const resultOptionLabel = (
  typeKey: string,
  resultType: string,
  useNaturalLanguage: boolean,
  options?: ResultLabelOptions,
): string => {
  const override = creditWalletOverride(typeKey, options, "picker");
  if (override) {
    return override;
  }
  if (typeKey === "display_banner") {
    return resultTypeKeyLabels()[typeKey] ?? typeKey;
  }
  return useNaturalLanguage
    ? resultTypeKeyLabels()[typeKey] ?? typeKey
    : legacyResultOptionLabel(typeKey, resultType);
};

export const bonusTypesForCategory = (
  category: BonusProductCategory,
): BonusTypeDefinition[] =>
  BONUS_TYPE_CATALOG.filter((entry) => entry.category === category);

export const bonusTypeById = (id: string): BonusTypeDefinition | undefined =>
  BONUS_TYPE_CATALOG.find((entry) => entry.id === id);
